/**
 * Pure rules for the AI narration usage report (Backlog 063): the month picker, labels, and the
 * percentages shown next to the ledger figures.
 */

import type {
  NarrationGenerationKind,
  NarrationGenerationStatus,
  NarrationUsageQuery,
  NarrationUsageTargetKind,
} from '@api/types/narration'

/** The picker value meaning "no month filter". */
export const ALL_TIME = 'all'

/** "yyyy-MM" of a date, in UTC (the ledger's months are UTC calendar months). */
export function toMonthKey(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
}

/** "October 2026" for "2026-10"; the input itself when it is not a month key. */
export function monthLabel(month: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month)
  if (!match) return month
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1))
  return date.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/** "Oct 26" for "2026-10", for the trend rows. */
export function shortMonthLabel(month: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month)
  if (!match) return month
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1))
  return `${date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })} ${match[1].slice(2)}`
}

/** This UTC month and the `count - 1` before it, newest first, as picker options. */
export function monthOptions(now: Date, count = 12): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = []
  for (let i = 0; i < count; i++) {
    const key = toMonthKey(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1)))
    out.push({ value: key, label: i === 0 ? `${monthLabel(key)} (this month)` : monthLabel(key) })
  }
  return out
}

/** The picker value as the API's `month` (null = all time). */
export function monthParam(value: string): string | null {
  return value === ALL_TIME ? null : value
}

/** A stable react-query key for one report call. */
export function usageKey(part: string, q: NarrationUsageQuery): unknown[] {
  return ['narration', 'usage', part, q.month ?? ALL_TIME, q.userId ?? '', q.search ?? '', q.page ?? 1, q.pageSize ?? 0]
}

/** 0–100, one decimal; null when there is nothing to divide by. */
export function percent(part: number, whole: number): number | null {
  if (!whole || whole <= 0) return null
  return Math.round((part / whole) * 1000) / 10
}

/** Share of requested characters that reuse did not bill: "12.5%" or "—". */
export function savingsLabel(saved: number, requested: number): string {
  const p = percent(saved, requested)
  return p == null ? '—' : `${p}%`
}

/** "1,234 / 10,000 characters" or "1,234 characters" when there is no allowance. */
export function allowanceLabel(used: number, allowance: number | null | undefined): string {
  return allowance && allowance > 0
    ? `${used.toLocaleString('en-US')} / ${allowance.toLocaleString('en-US')} characters`
    : `${used.toLocaleString('en-US')} characters`
}

/** 0–100 for a progress bar, capped; null when there is no allowance. */
export function allowanceFraction(used: number, allowance: number | null | undefined): number | null {
  if (!allowance || allowance <= 0) return null
  return Math.min(100, Math.max(0, (used / allowance) * 100))
}

const KIND_LABELS: Record<NarrationGenerationKind, string> = {
  node: 'Text step',
  audioNode: 'Audio step',
  storyDescription: 'Story description',
  adventureDescription: 'Adventure description',
  preview: 'Preview',
}

export function generationKindLabel(kind: string): string {
  return KIND_LABELS[kind as NarrationGenerationKind] ?? kind
}

const TARGET_LABELS: Record<NarrationUsageTargetKind, string> = {
  adventure: 'Adventure',
  story: 'Story',
  preview: 'Previews',
}

export function targetKindLabel(kind: string): string {
  return TARGET_LABELS[kind as NarrationUsageTargetKind] ?? kind
}

/** A target's title, or what to show when it has since been deleted. */
export function targetTitle(kind: string, title: string | null): string {
  if (kind === 'preview') return 'Previews (no target)'
  return title?.trim() ? title : `Deleted ${kind === 'story' ? 'Story' : 'adventure'}`
}

const STATUS_LABELS: Record<NarrationGenerationStatus, { label: string; color: 'success' | 'info' | 'error' | 'warning' }> = {
  succeeded: { label: 'Generated', color: 'success' },
  reused: { label: 'Reused', color: 'info' },
  failed: { label: 'Failed', color: 'error' },
  pending: { label: 'Pending', color: 'warning' },
}

export function statusChip(status: string): { label: string; color: 'success' | 'info' | 'error' | 'warning' | 'default' } {
  return STATUS_LABELS[status as NarrationGenerationStatus] ?? { label: status, color: 'default' }
}

/** Largest value in the trend, for bar widths (never 0). */
export function trendMax(values: number[]): number {
  return Math.max(1, ...values)
}
